import { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { dateIt } from "@/lib/format";
import { Card, CardContent } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { LifeBuoy, Plus, MessageSquare, Shield, Send, RefreshCw } from "lucide-react";

const CATEGORIES = [
  { v: "generale", l: "Generale" },
  { v: "ordine", l: "Ordine" },
  { v: "pagamento", l: "Pagamento" },
  { v: "iscrizione", l: "Iscrizione" },
  { v: "tecnico", l: "Tecnico" },
  { v: "altro", l: "Altro" },
];
const PRIORITIES = [
  { v: "bassa", l: "Bassa" },
  { v: "normale", l: "Normale" },
  { v: "alta", l: "Alta" },
];
const STATUS_COLOR = {
  aperto: "bg-amber-100 text-amber-800",
  in_lavorazione: "bg-blue-100 text-blue-800",
  risolto: "bg-emerald-100 text-emerald-800",
  chiuso: "bg-slate-200 text-slate-700",
};
const STATUS_LABEL = {
  aperto: "Aperto", in_lavorazione: "In lavorazione", risolto: "Risolto", chiuso: "Chiuso",
};
const PRIORITY_COLOR = {
  bassa: "bg-slate-100 text-slate-700",
  normale: "bg-blue-50 text-blue-700",
  alta: "bg-red-100 text-red-800",
};

export default function Support() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [tickets, setTickets] = useState([]);
  const [statusFilter, setStatusFilter] = useState("all");
  const [openCreate, setOpenCreate] = useState(false);
  const [selected, setSelected] = useState(null); // ticket detail object
  const [reply, setReply] = useState("");
  const [sending, setSending] = useState(false);
  const [newT, setNewT] = useState({ subject: "", body: "", category: "generale", priority: "normale" });

  const load = useCallback(async () => {
    const params = {};
    if (statusFilter !== "all") params.status = statusFilter;
    const { data } = await api.get("/tickets", { params });
    setTickets(data);
  }, [statusFilter]);
  useEffect(() => { load(); }, [load]);

  const openTicket = async (id) => {
    const { data } = await api.get(`/tickets/${id}`);
    setSelected(data);
  };

  const createTicket = async () => {
    if (!newT.subject.trim() || newT.subject.trim().length < 3) {
      toast.error("Oggetto obbligatorio (min 3 caratteri)"); return;
    }
    if (!newT.body.trim() || newT.body.trim().length < 3) {
      toast.error("Descrivi il problema (min 3 caratteri)"); return;
    }
    try {
      const { data } = await api.post("/tickets", newT);
      toast.success(`Ticket ${data.number} creato`);
      setOpenCreate(false);
      setNewT({ subject: "", body: "", category: "generale", priority: "normale" });
      await load();
      openTicket(data.id);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore creazione ticket");
    }
  };

  const sendReply = async () => {
    if (!reply.trim()) return;
    setSending(true);
    try {
      await api.post(`/tickets/${selected.id}/messages`, { body: reply });
      setReply("");
      const { data } = await api.get(`/tickets/${selected.id}`);
      setSelected(data);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore invio messaggio");
    } finally { setSending(false); }
  };

  const changeStatus = async (newStatus) => {
    try {
      await api.put(`/tickets/${selected.id}/status`, { status: newStatus });
      toast.success(`Ticket ${STATUS_LABEL[newStatus] || newStatus}`);
      const { data } = await api.get(`/tickets/${selected.id}`);
      setSelected(data);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore aggiornamento stato");
    }
  };

  const counters = useMemo(() => {
    const c = { aperto: 0, in_lavorazione: 0, risolto: 0, chiuso: 0 };
    tickets.forEach((t) => { if (c[t.status] !== undefined) c[t.status]++; });
    return c;
  }, [tickets]);

  return (
    <div className="space-y-6" data-testid="support-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-display text-3xl font-extrabold text-slate-900 flex items-center gap-2">
            <LifeBuoy className="w-7 h-7 text-amber-600" /> Assistenza · Ticket
          </h1>
          <p className="text-slate-500 mt-1">
            {isAdmin ? "Gestisci tutti i ticket aperti dai partner." : "Apri una richiesta per ricevere assistenza dal nostro team."}
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={load} data-testid="support-refresh"><RefreshCw className="w-4 h-4 mr-1" /> Aggiorna</Button>
          {!isAdmin && (
            <Button onClick={() => setOpenCreate(true)} className="bg-amber-500 hover:bg-amber-600 text-white" data-testid="support-new">
              <Plus className="w-4 h-4 mr-1" /> Nuovo ticket
            </Button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {Object.entries(counters).map(([k, v]) => (
          <Card key={k} className="border-slate-200">
            <CardContent className="p-4">
              <div className="text-xs uppercase tracking-widest font-bold text-slate-500">{STATUS_LABEL[k]}</div>
              <div className="font-display text-2xl font-extrabold text-[#0F2942] mt-1">{v}</div>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="flex items-center gap-3">
        <Label className="text-xs">Stato:</Label>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-48" data-testid="support-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tutti</SelectItem>
            <SelectItem value="aperto">Aperti</SelectItem>
            <SelectItem value="in_lavorazione">In lavorazione</SelectItem>
            <SelectItem value="risolto">Risolti</SelectItem>
            <SelectItem value="chiuso">Chiusi</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <Card className="border-slate-200"><CardContent className="p-0 overflow-x-auto">
        <Table>
          <TableHeader><TableRow>
            <TableHead>Numero</TableHead>
            <TableHead>Oggetto</TableHead>
            {isAdmin && <TableHead>Partner</TableHead>}
            <TableHead>Categoria</TableHead>
            <TableHead>Priorità</TableHead>
            <TableHead>Messaggi</TableHead>
            <TableHead>Ultimo msg</TableHead>
            <TableHead>Stato</TableHead>
            <TableHead></TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {tickets.map((t) => (
              <TableRow key={t.id} data-testid={`ticket-row-${t.number}`}>
                <TableCell className="font-mono text-xs text-slate-600">{t.number}</TableCell>
                <TableCell className="font-medium">{t.subject}</TableCell>
                {isAdmin && <TableCell>{t.partner_name || "-"}</TableCell>}
                <TableCell><Badge variant="outline">{t.category}</Badge></TableCell>
                <TableCell><Badge className={`${PRIORITY_COLOR[t.priority]} hover:${PRIORITY_COLOR[t.priority]}`}>{t.priority}</Badge></TableCell>
                <TableCell className="text-xs">
                  <span className="inline-flex items-center gap-1 text-slate-600">
                    <MessageSquare className="w-3 h-3" /> {t.messages_count || 0}
                  </span>
                </TableCell>
                <TableCell className="text-xs text-slate-500 whitespace-nowrap">{dateIt(t.last_message_at)}</TableCell>
                <TableCell>
                  <Badge className={`${STATUS_COLOR[t.status]} hover:${STATUS_COLOR[t.status]}`}>{STATUS_LABEL[t.status] || t.status}</Badge>
                </TableCell>
                <TableCell>
                  <Button variant="outline" size="sm" onClick={() => openTicket(t.id)} data-testid={`ticket-open-${t.number}`}>
                    Apri
                  </Button>
                </TableCell>
              </TableRow>
            ))}
            {tickets.length === 0 && (
              <TableRow><TableCell colSpan={isAdmin ? 9 : 8} className="text-center text-slate-500 py-10">
                Nessun ticket{!isAdmin ? " · apri la tua prima richiesta cliccando \"Nuovo ticket\"" : ""}.
              </TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </CardContent></Card>

      {/* Create dialog */}
      <Dialog open={openCreate} onOpenChange={setOpenCreate}>
        <DialogContent className="max-w-xl" data-testid="support-create-dialog">
          <DialogHeader><DialogTitle>Nuovo ticket di assistenza</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Oggetto *</Label>
              <Input value={newT.subject} onChange={(e) => setNewT({ ...newT, subject: e.target.value })} placeholder="Es. Problema di pagamento su ordine ORD-..." data-testid="support-new-subject" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Categoria</Label>
                <Select value={newT.category} onValueChange={(v) => setNewT({ ...newT, category: v })}>
                  <SelectTrigger data-testid="support-new-category"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map((c) => <SelectItem key={c.v} value={c.v}>{c.l}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Priorità</Label>
                <Select value={newT.priority} onValueChange={(v) => setNewT({ ...newT, priority: v })}>
                  <SelectTrigger data-testid="support-new-priority"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {PRIORITIES.map((p) => <SelectItem key={p.v} value={p.v}>{p.l}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="text-xs">Descrizione *</Label>
              <Textarea rows={5} value={newT.body} onChange={(e) => setNewT({ ...newT, body: e.target.value })} placeholder="Descrivi il problema o la richiesta…" data-testid="support-new-body" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpenCreate(false)}>Annulla</Button>
            <Button onClick={createTicket} className="bg-amber-500 hover:bg-amber-600 text-white" data-testid="support-new-submit">Invia ticket</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Thread dialog */}
      <Dialog open={!!selected} onOpenChange={(v) => { if (!v) setSelected(null); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="support-thread-dialog">
          {selected && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2 flex-wrap">
                  <span className="font-mono text-xs text-slate-500">{selected.number}</span>
                  <span>{selected.subject}</span>
                  <Badge className={STATUS_COLOR[selected.status]}>{STATUS_LABEL[selected.status]}</Badge>
                </DialogTitle>
              </DialogHeader>
              <div className="flex items-center gap-2 text-xs text-slate-500 flex-wrap">
                <Badge variant="outline">{selected.category}</Badge>
                <Badge className={PRIORITY_COLOR[selected.priority]}>{selected.priority}</Badge>
                {isAdmin && selected.partner_name && <span>· Partner: <b>{selected.partner_name}</b></span>}
                <span>· {selected.messages_count} msg · aggiornato {dateIt(selected.updated_at)}</span>
              </div>

              <div className="space-y-3 mt-4 max-h-[46vh] overflow-y-auto pr-2">
                {(selected.messages || []).map((m) => {
                  const isAdm = m.author_role === "admin";
                  return (
                    <div key={m.id} className={`flex ${isAdm ? "justify-start" : "justify-end"}`}>
                      <div className={`max-w-[82%] rounded-xl px-4 py-2.5 border ${isAdm ? "bg-blue-50/70 border-blue-200" : "bg-amber-50/60 border-amber-200"}`}>
                        <div className="flex items-center gap-2 text-xs text-slate-700">
                          {isAdm ? <Shield className="w-3 h-3 text-blue-600" /> : <LifeBuoy className="w-3 h-3 text-amber-600" />}
                          <span className="font-semibold">{m.author_name || m.author_email}</span>
                          <span className="text-slate-400">· {dateIt(m.created_at)}</span>
                        </div>
                        <div className="mt-1 text-sm text-slate-900 whitespace-pre-wrap">{m.body}</div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {selected.status !== "chiuso" ? (
                <div className="pt-4 border-t mt-2 space-y-2">
                  <Textarea rows={3} placeholder="Scrivi un messaggio…" value={reply} onChange={(e) => setReply(e.target.value)} data-testid="support-reply-input" />
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <div className="flex gap-2 flex-wrap">
                      {isAdmin && (
                        <>
                          <Button variant="outline" size="sm" onClick={() => changeStatus("in_lavorazione")} disabled={selected.status === "in_lavorazione"} data-testid="support-set-working">In lavorazione</Button>
                          <Button variant="outline" size="sm" onClick={() => changeStatus("risolto")} data-testid="support-set-resolved">Marca risolto</Button>
                          <Button variant="outline" size="sm" onClick={() => changeStatus("chiuso")} data-testid="support-set-closed">Chiudi</Button>
                        </>
                      )}
                      {!isAdmin && selected.created_by_role === "partner" && (
                        <Button variant="outline" size="sm" onClick={() => changeStatus("chiuso")} data-testid="support-partner-close">Chiudi ticket</Button>
                      )}
                    </div>
                    <Button onClick={sendReply} disabled={!reply.trim() || sending} className="bg-amber-500 hover:bg-amber-600 text-white" data-testid="support-send-reply">
                      <Send className="w-4 h-4 mr-1" /> {sending ? "Invio…" : "Invia risposta"}
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="pt-4 border-t mt-2 flex items-center justify-between gap-2 flex-wrap">
                  <div className="text-sm text-slate-500">Ticket chiuso.</div>
                  <Button variant="outline" size="sm" onClick={() => changeStatus("aperto")} data-testid="support-reopen">Riapri ticket</Button>
                </div>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
